import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button, TextField,
  Chip, Rating, Divider, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Avatar, CircularProgress, InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import Luggage from '@mui/icons-material/Luggage';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SettingsIcon from '@mui/icons-material/Settings';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StarIcon from '@mui/icons-material/Star';
import { TZ, todayMayotte } from '../../utils/date';

const CarRentalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingData, setBookingData] = useState({
    pickupDate: '', returnDate: '',
    pickupLocation: 'Aéroport de Dzaoudzi-Pamandzi',
    returnLocation: 'Aéroport de Dzaoudzi-Pamandzi',
    driverName: '', driverPhone: '', driverLicense: '',
  });
  const [bookedRanges, setBookedRanges] = useState([]);
  const [dateError, setDateError] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');

  useEffect(() => { loadCarDetails(); loadBookedDates(); }, [id]); // eslint-disable-line

  const loadCarDetails = async () => {
    try { const r = await api.get(`/car-rentals/${id}`); setCar(r.data.car || null); }
    catch { setCar(null); } finally { setLoading(false); }
  };
  const loadBookedDates = async () => {
    try { const r = await api.get(`/car-rentals/${id}/booked-dates`); setBookedRanges(r.data.bookedRanges || []); }
    catch { setBookedRanges([]); }
  };

  const hasConflict = (pickup, ret) => {
    if (!pickup || !ret) return false;
    const p = new Date(pickup), r = new Date(ret);
    return bookedRanges.some(({ start, end }) => p < new Date(end) && r > new Date(start));
  };

  const today = todayMayotte();

  const handleDateChange = (field, value) => {
    const u = { ...bookingData, [field]: value };
    if (field === 'pickupDate' && u.returnDate && value > u.returnDate) u.returnDate = value;
    setBookingData(u);
    const p = field === 'pickupDate' ? value : u.pickupDate;
    const r = field === 'returnDate' ? value : u.returnDate;
    setDateError(p && r && hasConflict(p, r) ? 'Ces dates chevauchent une réservation existante' : '');
  };

  const days = bookingData.pickupDate && bookingData.returnDate
    ? Math.ceil((new Date(bookingData.returnDate) - new Date(bookingData.pickupDate)) / 86400000) : 0;
  const rentalCost = days > 0 && car ? days * car.pricePerDay : 0;
  const insuranceCost = days > 0 && car ? days * car.insurance : 0;
  const totalPrice = rentalCost + insuranceCost;

  const handleBooking = async () => {
    if (!bookingData.pickupDate || !bookingData.returnDate || !bookingData.driverName || !bookingData.driverPhone || !bookingData.driverLicense) {
      setBookingError('Veuillez remplir tous les champs obligatoires'); return;
    }
    if (dateError) return;
    setBookingLoading(true); setBookingError('');
    try {
      await api.post('/car-rentals/bookings', { carId: car.id, ...bookingData });
      navigate('/car-rental/bookings');
    } catch (err) {
      setBookingError(err.response?.data?.message || 'Erreur lors de la réservation');
    } finally { setBookingLoading(false); }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': { bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '12px', '& fieldset': { borderColor: 'rgba(85,37,131,0.15)' }, '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#552583' } },
    '& .MuiInputLabel-root': { color: '#4a4a4a' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#552583' },
    '& input': { color: '#1a1a1a' },
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress sx={{ color: '#552583' }} /></Box>;

  if (!car) return (
    <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
      <DirectionsCarIcon sx={{ fontSize: 80, color: 'rgba(85,37,131,0.15)', mb: 2 }} />
      <Typography variant="h5" fontWeight={700} sx={{ color: '#1a1a1a', mb: 1 }}>Véhicule introuvable</Typography>
      <Button onClick={() => navigate('/car-rental')} sx={{ color: '#552583', textTransform: 'none', fontWeight: 700 }}>
        Retour aux locations
      </Button>
    </Container>
  );

  const images = car.images?.length > 0 ? car.images : [car.image];

  return (
    <Box sx={{ background: '#E4F0F6', minHeight: '100vh' }}>

      {/* Bouton retour */}
      <Box sx={{ background: 'linear-gradient(135deg, #552583 0%, #3a1760 100%)', py: 2 }}>
        <Container maxWidth="lg">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/car-rental')}
            sx={{ color: 'rgba(255,255,255,0.75)', textTransform: 'none', fontWeight: 600, borderRadius: '10px', '&:hover': { color: '#FDB927', bgcolor: 'rgba(255,255,255,0.08)' } }}>
            Retour aux résultats
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={4}>

          {/* ── Colonne gauche ── */}
          <Grid item xs={12} md={8}>

            {/* Galerie */}
            <Card sx={{ mb: 3, borderRadius: '20px !important', overflow: 'hidden', background: 'white', border: '1px solid rgba(85,37,131,0.08)', boxShadow: '0 4px 20px rgba(85,37,131,0.08)', '&:hover': { transform: 'none' } }}>
              <Box sx={{ position: 'relative', height: { xs: 280, md: 420 }, overflow: 'hidden', bgcolor: 'rgba(85,37,131,0.05)' }}>
                <Box component="img" src={images[selectedImage]} alt={`${car.brand} ${car.model}`}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.5) 100%)' }} />
                <Box sx={{ position: 'absolute', bottom: 20, left: 24, right: 24 }}>
                  <Typography variant="h3" fontWeight={900} sx={{ color: 'white', textShadow: '0 2px 12px rgba(0,0,0,0.5)', fontSize: { xs: '1.8rem', md: '2.5rem' }, textTransform: 'none', letterSpacing: 0 }}>
                    {car.brand} {car.model}
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={car.category} size="small" sx={{ bgcolor: '#552583', color: 'white', fontWeight: 700 }} />
                    <Chip label={car.year} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)', color: '#1a1a1a', fontWeight: 600 }} />
                    <Chip label={car.transmission} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)', color: '#1a1a1a', fontWeight: 600 }} />
                  </Box>
                </Box>
              </Box>
            </Card>

            {/* Miniatures */}
            {images.length > 1 && (
              <Box display="flex" gap={1.5} mb={3} sx={{ overflowX: 'auto', pb: 1 }}>
                {images.map((img, idx) => (
                  <Box key={idx} onClick={() => setSelectedImage(idx)} sx={{
                    width: 100, height: 70, borderRadius: 2, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                    border: selectedImage === idx ? '3px solid #552583' : '3px solid transparent',
                    opacity: selectedImage === idx ? 1 : 0.55, transition: 'all 0.25s', '&:hover': { opacity: 1 },
                  }}>
                    <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            )}

            {/* Specs */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { icon: <PeopleIcon sx={{ color: '#552583' }} />, label: 'Places', value: car.seats },
                { icon: <Luggage sx={{ color: '#552583' }} />, label: 'Bagages', value: car.luggage },
                { icon: <LocalGasStationIcon sx={{ color: '#FDB927' }} />, label: 'Carburant', value: car.fuel },
                { icon: <SettingsIcon sx={{ color: '#552583' }} />, label: 'Transmission', value: car.transmission },
                ...(car.airConditioning ? [{ icon: <AcUnitIcon sx={{ color: '#FDB927' }} />, label: 'Climatisation', value: 'Oui' }] : []),
              ].map((s, i) => (
                <Grid item xs={6} sm={4} md={2.4} key={i}>
                  <Box sx={{ p: 2, borderRadius: 3, textAlign: 'center', background: 'white', border: '1px solid rgba(85,37,131,0.1)', boxShadow: '0 2px 8px rgba(85,37,131,0.05)', transition: 'all 0.25s', '&:hover': { border: '1px solid rgba(85,37,131,0.3)', transform: 'translateY(-2px)' } }}>
                    <Box sx={{ mb: 0.5 }}>{s.icon}</Box>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a1a' }}>{s.value}</Typography>
                    <Typography variant="caption" sx={{ color: '#7a7a7a' }}>{s.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Description */}
            {car.description && (
              <Card sx={{ mb: 3, borderRadius: '16px !important', background: 'white', border: '1px solid rgba(85,37,131,0.08)', boxShadow: 'none', '&:hover': { transform: 'none' } }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 2 }}>Description</Typography>
                  <Typography variant="body1" sx={{ color: '#3a3a3a', lineHeight: 1.8 }}>{car.description}</Typography>
                </CardContent>
              </Card>
            )}

            {/* Équipements */}
            {car.features?.length > 0 && (
              <Card sx={{ mb: 3, borderRadius: '16px !important', background: 'white', border: '1px solid rgba(85,37,131,0.08)', boxShadow: 'none', '&:hover': { transform: 'none' } }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 2 }}>Équipements inclus</Typography>
                  <Grid container spacing={1}>
                    {car.features.map((f, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ py: 0.7 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: '#552583' }} />
                          <Typography variant="body2" sx={{ color: '#2a2a2a' }}>{f}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Propriétaire */}
            <Card sx={{ borderRadius: '16px !important', background: 'white', border: '1px solid rgba(85,37,131,0.08)', boxShadow: 'none', '&:hover': { transform: 'none' } }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 2 }}>Propriétaire</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: '#552583', color: 'white', fontWeight: 800, fontSize: '1.3rem' }}>
                    {car.owner.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight={700} sx={{ color: '#1a1a1a' }}>{car.owner.name}</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.3}>
                      <StarIcon sx={{ fontSize: 16, color: '#FDB927' }} />
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{car.owner.rating}</Typography>
                      <Typography variant="body2" sx={{ color: '#7a7a7a' }}>
                        • {car.owner.totalCars} véhicule{car.owner.totalCars > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Colonne droite — Réservation ── */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              position: { md: 'sticky' }, top: { md: 80 }, borderRadius: '20px !important',
              background: 'white', border: '1px solid rgba(85,37,131,0.12)',
              boxShadow: '0 8px 40px rgba(85,37,131,0.12)', '&:hover': { transform: 'none' },
            }}>
              <CardContent sx={{ p: 3 }}>
                {/* Prix */}
                <Box sx={{ mb: 3, textAlign: 'center', p: 2.5, borderRadius: '14px', background: 'linear-gradient(135deg, rgba(85,37,131,0.06) 0%, rgba(253,185,39,0.06) 100%)', border: '1px solid rgba(85,37,131,0.1)' }}>
                  <Box display="flex" alignItems="baseline" justifyContent="center" gap={0.5}>
                    <Typography variant="h2" fontWeight={900} sx={{ background: 'linear-gradient(135deg, #552583, #FDB927)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1 }}>
                      {car.pricePerDay}€
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4a4a4a' }}>/ jour</Typography>
                  </Box>
                  {car.rating > 0 && (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mt={1}>
                      <Rating value={car.rating} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" sx={{ color: '#4a4a4a' }}>({car.reviews} avis)</Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ mb: 3, borderColor: 'rgba(85,37,131,0.1)' }} />

                {/* Dates */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1a1a1a' }}>Dates de location</Typography>
                  <TextField fullWidth size="small" label="Date de prise" type="date"
                    value={bookingData.pickupDate} onChange={e => handleDateChange('pickupDate', e.target.value)}
                    InputLabelProps={{ shrink: true }} inputProps={{ min: today }} error={!!dateError}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 16, color: '#552583' }} /></InputAdornment> }}
                    sx={{ ...fieldSx, mb: 1.5 }} />
                  <TextField fullWidth size="small" label="Date de retour" type="date"
                    value={bookingData.returnDate} onChange={e => handleDateChange('returnDate', e.target.value)}
                    InputLabelProps={{ shrink: true }} inputProps={{ min: bookingData.pickupDate || today }} error={!!dateError}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 16, color: '#552583' }} /></InputAdornment> }}
                    sx={fieldSx} />
                  {dateError && <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{dateError}</Alert>}
                </Box>

                {/* Dates indispo */}
                {bookedRanges.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#7a7a7a', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <BlockIcon sx={{ fontSize: 14 }} /> Dates indisponibles
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {bookedRanges.map((r, i) => (
                        <Chip key={i}
                          label={`${new Date(r.start).toLocaleDateString('fr-FR', { timeZone: TZ, day: 'numeric', month: 'short' })} - ${new Date(r.end).toLocaleDateString('fr-FR', { timeZone: TZ, day: 'numeric', month: 'short' })}`}
                          size="small" sx={{ bgcolor: 'rgba(231,76,60,0.08)', color: '#e74c3c', fontSize: '0.7rem', border: '1px solid rgba(231,76,60,0.2)' }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Récap prix */}
                {days > 0 && (
                  <Box sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'rgba(85,37,131,0.04)', border: '1px solid rgba(85,37,131,0.1)' }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" sx={{ color: '#4a4a4a' }}>{car.pricePerDay}€ × {days} jour{days > 1 ? 's' : ''}</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{rentalCost.toFixed(0)}€</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Assurance ({car.insurance}€/j)</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{insuranceCost.toFixed(0)}€</Typography>
                    </Box>
                    <Divider sx={{ my: 1.5, borderColor: 'rgba(85,37,131,0.1)' }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1" fontWeight={800} sx={{ color: '#1a1a1a' }}>Total</Typography>
                      <Typography variant="body1" fontWeight={900} sx={{ color: '#552583', fontSize: '1.1rem' }}>{totalPrice.toFixed(0)}€</Typography>
                    </Box>
                  </Box>
                )}


                <Button fullWidth variant="contained" size="large"
                  onClick={() => setBookingDialog(true)}
                  disabled={!car.available || !!dateError || days <= 0}
                  sx={{
                    py: 1.8,
                    background: days > 0 ? 'linear-gradient(135deg, #552583, #FDB927)' : 'rgba(85,37,131,0.1)',
                    color: 'white', fontWeight: 800, fontSize: '1rem',
                    borderRadius: '14px', textTransform: 'none',
                    boxShadow: days > 0 ? '0 8px 30px rgba(85,37,131,0.3)' : 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #33154f, #c98a00)', transform: 'translateY(-2px)', boxShadow: '0 12px 40px rgba(85,37,131,0.4)' },
                    '&:disabled': { background: 'rgba(85,37,131,0.08)', color: 'rgba(0,0,0,0.3)' },
                  }}>
                  {days > 0 ? `Réserver — ${totalPrice.toFixed(0)}€` : 'Sélectionnez vos dates'}
                </Button>

                {/* Badges confiance */}
                <Box display="flex" justifyContent="center" gap={3} mt={2.5}>
                  {[{ icon: <EventAvailableIcon sx={{ fontSize: 15 }} />, text: 'Annulation gratuite' }, { icon: <VerifiedUserIcon sx={{ fontSize: 15 }} />, text: 'Vérifié' }].map((b, i) => (
                    <Box key={i} display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ color: '#7a7a7a' }}>{b.icon}</Box>
                      <Typography variant="caption" sx={{ color: '#7a7a7a' }}>{b.text}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* ── Dialog réservation ── */}
      <Dialog open={bookingDialog} onClose={() => setBookingDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '20px', background: '#fff', border: '1px solid rgba(85,37,131,0.15)' } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(85,37,131,0.08)' }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a' }}>Finaliser la réservation</Typography>
          <Typography variant="body2" sx={{ color: '#4a4a4a' }}>
            {car.brand} {car.model} — {days} jour{days > 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {bookingError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{bookingError}</Alert>}

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1a1a1a' }}>Informations du conducteur</Typography>
          <TextField fullWidth size="small" label="Nom complet *" value={bookingData.driverName}
            onChange={e => setBookingData({ ...bookingData, driverName: e.target.value })} sx={{ ...fieldSx, mb: 1.5 }} />
          <TextField fullWidth size="small" label="Téléphone *" value={bookingData.driverPhone}
            onChange={e => setBookingData({ ...bookingData, driverPhone: e.target.value })} sx={{ ...fieldSx, mb: 1.5 }} />
          <TextField fullWidth size="small" label="Numéro de permis *" value={bookingData.driverLicense}
            onChange={e => setBookingData({ ...bookingData, driverLicense: e.target.value })} sx={{ ...fieldSx, mb: 2 }} />

          <Divider sx={{ my: 2, borderColor: 'rgba(85,37,131,0.08)' }} />

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1a1a1a' }}>Lieux de prise et retour</Typography>
          <TextField fullWidth size="small" label="Lieu de prise" value={bookingData.pickupLocation}
            onChange={e => setBookingData({ ...bookingData, pickupLocation: e.target.value })} sx={{ ...fieldSx, mb: 1.5 }} />
          <TextField fullWidth size="small" label="Lieu de retour" value={bookingData.returnLocation}
            onChange={e => setBookingData({ ...bookingData, returnLocation: e.target.value })} sx={fieldSx} />

          {/* Récap */}
          <Box sx={{ mt: 3, p: 2.5, bgcolor: 'rgba(85,37,131,0.04)', borderRadius: 2, border: '1px solid rgba(85,37,131,0.1)' }}>
            <Box display="flex" justifyContent="space-between" mb={0.8}>
              <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Location ({days}j)</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{rentalCost.toFixed(0)}€</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={0.8}>
              <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Assurance</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{insuranceCost.toFixed(0)}€</Typography>
            </Box>
            <Divider sx={{ my: 1.2, borderColor: 'rgba(85,37,131,0.1)' }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight={800} sx={{ color: '#1a1a1a' }}>Total</Typography>
              <Typography variant="body1" fontWeight={900} sx={{ color: '#552583', fontSize: '1.1rem' }}>{totalPrice.toFixed(0)}€</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid rgba(85,37,131,0.08)', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setBookingDialog(false)} disabled={bookingLoading}
            sx={{ color: '#4a4a4a', textTransform: 'none', fontWeight: 600 }}>
            Annuler
          </Button>
          <Button onClick={handleBooking} variant="contained" disabled={bookingLoading}
            startIcon={bookingLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
            sx={{ background: 'linear-gradient(135deg, #552583, #FDB927)', color: 'white', fontWeight: 700, px: 4, borderRadius: '12px', textTransform: 'none', boxShadow: '0 4px 20px rgba(85,37,131,0.3)', '&:hover': { background: 'linear-gradient(135deg, #33154f, #c98a00)' } }}>
            {bookingLoading ? 'Réservation…' : `Confirmer — ${totalPrice.toFixed(0)}€`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarRentalDetail;
